import { httpClient, PaginationParams } from '../core/http';
import {
  ToolRecord,
  ToolRecordCreateRequest,
  ToolRecordHistoryEntry,
  ToolRecordUpdateRequest,
} from '../types';

export type ToolRecordListResponse = {
  items: ToolRecord[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
};

export type RuntimeInitRequest = {
  context?: Record<string, unknown>;
};

export type RuntimeInitResponse = {
  tool_id: string;
  component_tree: Record<string, unknown>;
  action_map: Record<string, unknown>;
  view_model: Record<string, unknown>;
  state_token: string;
};

export type RuntimeActionRequest = {
  action_id: string;
  payload?: Record<string, unknown>;
  state_token?: string;
};

export type RuntimeActionResponse = {
  status: string;
  action_id: string;
  state_token: string;
  result: Record<string, unknown>;
  view_model_delta: Record<string, unknown>;
};

export class ToolRuntimeService {
  private basePath(toolId: string): string {
    return `/v1/tools/${toolId}/records`;
  }

  private runtimeBasePath(toolId: string): string {
    return `/v1/tools/${toolId}/runtime`;
  }

  private normalizeError(error: unknown): Error {
    if (error && typeof error === 'object') {
      const maybeAxios = error as {
        response?: { data?: { detail?: string; message?: string } };
        message?: string;
      };
      const detail = maybeAxios.response?.data?.detail;
      if (typeof detail === 'string' && detail.trim()) {
        return new Error(detail);
      }
      const msg = maybeAxios.response?.data?.message;
      if (typeof msg === 'string' && msg.trim()) {
        return new Error(msg);
      }
      if (typeof maybeAxios.message === 'string' && maybeAxios.message.trim()) {
        return new Error(maybeAxios.message);
      }
    }
    return new Error('Erro ao executar operação de runtime.');
  }

  async listRecords(
    toolId: string,
    params?: PaginationParams & { entity_type?: string; include_inactive?: boolean }
  ): Promise<ToolRecordListResponse> {
    try {
      const response = await httpClient.get<ToolRecordListResponse>(
        this.basePath(toolId),
        { params: params || {} }
      );
      return response.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async initRuntime(
    toolId: string,
    payload?: RuntimeInitRequest
  ): Promise<RuntimeInitResponse> {
    try {
      const response = await httpClient.post<RuntimeInitResponse>(
        `${this.runtimeBasePath(toolId)}/init`,
        payload || {}
      );
      return response.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async executeRuntimeAction(
    toolId: string,
    payload: RuntimeActionRequest
  ): Promise<RuntimeActionResponse> {
    try {
      const response = await httpClient.post<RuntimeActionResponse>(
        `${this.runtimeBasePath(toolId)}/action`,
        payload
      );
      return response.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async getRecord(toolId: string, recordId: string): Promise<ToolRecord> {
    try {
      const response = await httpClient.get<ToolRecord>(
        `${this.basePath(toolId)}/${recordId}`
      );
      return response.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async createRecord(
    toolId: string,
    payload: ToolRecordCreateRequest
  ): Promise<ToolRecord> {
    try {
      const response = await httpClient.post<ToolRecord>(
        this.basePath(toolId),
        payload
      );
      return response.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async updateRecord(
    toolId: string,
    recordId: string,
    payload: ToolRecordUpdateRequest
  ): Promise<ToolRecord> {
    try {
      const response = await httpClient.put<ToolRecord>(
        `${this.basePath(toolId)}/${recordId}`,
        payload
      );
      return response.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async deleteRecord(toolId: string, recordId: string): Promise<void> {
    try {
      await httpClient.delete(`${this.basePath(toolId)}/${recordId}`);
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  async getRecordHistory(
    toolId: string,
    recordId: string
  ): Promise<ToolRecordHistoryEntry[]> {
    try {
      const response = await httpClient.get<ToolRecordHistoryEntry[]>(
        `${this.basePath(toolId)}/${recordId}/history`
      );
      return response.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }
}

export const toolRuntimeService = new ToolRuntimeService();
